import { useState } from "react";

interface QuoteRequestModalProps {
  factoryName: string;
  onClose: () => void;
}

export default function QuoteRequestModal({ factoryName, onClose }: QuoteRequestModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    whatsapp: "",
    country: "",
    product: "",
    quantity: "",
    budget: "",
    details: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl">
              <i className="ri-file-list-3-line text-orange-500 text-xl" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Request a Quote</h2>
              <p className="text-xs text-gray-500">from {factoryName}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-lock-line text-orange-500 text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-700">Your contact info is shared only with <strong>{factoryName}</strong>. They will reply to your email directly.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required placeholder="Your name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Company</label>
                <input name="company" value={form.company} onChange={handleChange} placeholder="Company name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email Address *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">WhatsApp</label>
                <input name="whatsapp" value={form.whatsapp} onChange={handleChange} placeholder="+1 234 567 8900" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Country *</label>
                <select name="country" value={form.country} onChange={handleChange} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors bg-white cursor-pointer">
                  <option value="">Select country</option>
                  {["USA", "Canada", "UK", "Germany", "Australia", "Japan", "France", "South Korea", "India", "Brazil", "UAE", "Singapore", "Other"].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product / Item *</label>
              <input name="product" value={form.product} onChange={handleChange} required placeholder="e.g. Wireless Earbuds OEM, 500 units" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quantity *</label>
                <input name="quantity" value={form.quantity} onChange={handleChange} required placeholder="e.g. 500 units" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Budget (USD)</label>
                <input name="budget" value={form.budget} onChange={handleChange} placeholder="e.g. $10,000" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Additional Details</label>
              <textarea
                name="details"
                value={form.details}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="Specifications, customization, branding requirements, delivery timeline..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-orange-400 transition-colors resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-0.5">{form.details.length}/500</p>
            </div>

            <button type="submit" className="w-full py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm">
              <i className="ri-send-plane-line mr-2" />Send Quote Request to {factoryName}
            </button>
            <p className="text-center text-xs text-gray-400">Factory typically responds within {factoryName === "PureGlow Labs" ? "1 hour" : "2-3 hours"}</p>
          </form>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Sent!</h3>
            <p className="text-sm text-gray-500 mb-1">Your request has been sent to <strong>{factoryName}</strong>.</p>
            <p className="text-sm text-gray-500 mb-6">They will review your requirements and send a competitive quotation to your email. You&apos;ll also receive a notification when they view your request.</p>
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-6 text-left">
              <p className="text-xs font-bold text-orange-800 mb-2 flex items-center gap-1.5">
                <i className="ri-notification-3-line" /> Email Notifications Enabled
              </p>
              <p className="text-xs text-orange-700">You&apos;ll receive an email alert when {factoryName} views your requirement and when they send a quotation.</p>
            </div>
            <button onClick={onClose} className="px-8 py-3 bg-gray-900 text-white font-bold rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
